/**
 * Wire schema for `GET /api/v4/executions` (`src/api/v4/action-routes.ts`) —
 * the action-history feed `MyActions` renders. Mirrors the server-side
 * `ActivityEntry` interface in `src/api/v4/activity/types.ts`; the server
 * builds each entry with `parseHandlerResult` / `parseExecutionJsonObject`
 * (see `v4-execution.ts`), so the nested shapes here reuse those schemas.
 */

import { z } from 'zod';
import { V4HandlerResultSchema, V4ExecutionJsonObjectSchema } from './v4-execution';

const OutcomeSchema = z.object({ status: z.string() });

export const ActivityEntrySchema = z.object({
  id: z.string(),
  kind: z.enum(['bound', 'unbound', 'bundle']),
  actionName: z.string(),
  /** completed | pending | cancelled | timed_out */
  outcomeStatus: z.string(),
  /** pending | dispatching | delivered | failed */
  deliveryStatus: z.string(),
  retryable: z.boolean(),
  handler: z.string(),
  handlerResult: V4HandlerResultSchema.optional(),
  errorMessage: z.string().optional(),
  /** artifact_card | global_menu | retry | api | bundle_action */
  source: z.string(),
  input: V4ExecutionJsonObjectSchema.optional(),
  handlerPayload: V4ExecutionJsonObjectSchema.optional(),
  handlerSummary: z.string().optional(),
  /** Unbound actions only: awaiting, receiving, completed, timed_out. */
  completionStatus: z.string().optional(),
  /** Bound only. */
  target: z
    .object({
      artifactId: z.string(),
      artifactSummary: z.string().optional(),
      blueprintKind: z.string(),
      blueprintDisplayName: z.string(),
      outcome: OutcomeSchema.optional(),
    })
    .optional(),
  /** Bundle only. */
  bundle: z
    .object({
      bundleKind: z.string(),
      correlationId: z.string(),
      outcome: OutcomeSchema.optional(),
    })
    .optional(),
  /** Unbound only. */
  correlation: z
    .object({
      correlationId: z.string(),
      artifactCount: z.number(),
      expectedArtifactCount: z.number().optional(),
    })
    .optional(),
  createdAt: z.string(),
  completedAt: z.string().optional(),
});
export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

export const ActivityEntriesResponseSchema = z.array(ActivityEntrySchema);
